import { JSX, useContext, useMemo, useState } from "react";
import { ContractEntityColorTheme } from "@app/overviews/market-expansion-dashboard/contexts/ContractEntityColorThemeContext";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { ProviderFootprintMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/ProviderFootprintMapDataContext";
import { ProviderLandscapeMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/ProviderLandscapeMapDataContext";
import { ContractEntityServiceAreaDetails } from "@app/overviews/market-expansion-dashboard/shared/ContractEntityServiceAreaDetails";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ColumnConfig, numberSort, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";

export interface ContractEntityServiceAreaDetailsWaffleToppings {
  casesPercentage: string;
  contractEntity: JSX.Element;
  groupingId: number;
  key: string;
  providerCount: string;
  totalCases: string;
}

const columnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  contractEntity: {
    displayText: "Contract Entity",
    testId: "contract-entity-name",
  },
  providerCount: {
    displayText: "Hospital Count",
    testId: "provider-count",
  },
  totalCases: {
    displayText: "Total Cases",
    testId: "total-cases",
  },
  casesPercentage: {
    displayText: "% Cases",
    testId: "cases-percentage",
  },
  /* oxlint-enable sort-keys */
};

const sortContractEntityDetailRows = (
  segment: ContractEntityServiceAreaDetailsWaffleToppings[],
  column: keyof ContractEntityServiceAreaDetailsWaffleToppings,
  direction: SortDirections,
): ContractEntityServiceAreaDetailsWaffleToppings[] => {
  if (direction === SortDirections.None) return segment;
  const multiplier = direction === SortDirections.Asc ? 1 : -1;
  switch (column) {
    case "contractEntity":
      return [...segment].sort((a, b) => (a.key > b.key ? 1 : -1) * multiplier);
    case "providerCount":
    case "totalCases":
      return numberSort<ContractEntityServiceAreaDetailsWaffleToppings>(segment, column, multiplier);
    case "casesPercentage":
      // casesPercentage is directly calculated from totalCases; sort by that column instead since it is more granular
      return numberSort<ContractEntityServiceAreaDetailsWaffleToppings>(segment, "totalCases", multiplier);
    default:
      return segment;
  }
};

export const formatCasesPercentage = (cases: number, totalCases: number): string => {
  const casesPercentageRaw = (cases / totalCases) * 100;
  if (Number.isNaN(casesPercentageRaw) || casesPercentageRaw === 0) {
    return "0%";
  } else if (casesPercentageRaw < 1) {
    return "<1%";
  }
  return casesPercentageRaw.toFixed(0) + "%";
};

const ContractEntityServiceAreaDetailsTable = (): JSX.Element => {
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const providerLandscapeContext = useContext(ProviderLandscapeMapDataContext);
  const providerFootprintContext = useContext(ProviderFootprintMapDataContext);

  const { getColor } = ContractEntityColorTheme.useMapColorTheme();

  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Desc);
  const [sortedColumn, setSortedColumn] = useState<keyof ContractEntityServiceAreaDetailsWaffleToppings>("totalCases");

  const contractEntityDetails = useMemo(() => {
    const contractEntityDetailsMap = new Map<number, ContractEntityServiceAreaDetails>();
    providerLandscapeContext.contractEntityServiceAreaCountyLevelSummaries.forEach((ceSummary) => {
      const groupingId = ceSummary.groupingId;
      if (!contractEntityDetailsMap.has(groupingId)) {
        const details = {
          contractEntityName: ceSummary.contractEntityName,
          groupingId,
          providerCount: providerFootprintContext.providerFootprintData?.get(groupingId)?.length ?? 0,
          totalCases: providerLandscapeContext.serviceAreaCasesByGroupingId.get(groupingId) ?? 0,
        };
        contractEntityDetailsMap.set(groupingId, details);
      }
    });

    return [...contractEntityDetailsMap.values()];
  }, [providerLandscapeContext.contractEntityServiceAreaCountyLevelSummaries, providerLandscapeContext.serviceAreaCasesByGroupingId, providerFootprintContext.providerFootprintData]);

  const totalServiceAreaCases = useMemo(() => {
    return contractEntityDetails.reduce((sum, ce) => sum + ce.totalCases, 0);
  }, [contractEntityDetails]);

  const getContractEntityNameCell = (contractEntityName: string, groupingId: number) => {
    return (
      <span className="contract-entity-cell-with-icon demibold">
        <QesIcon
          className="me-3"
          iconName={QesIcons.Circle}
          isDecorative
          pathStyle={{
            fill: getColor(providerLandscapeContext.getContractEntityColorSlotKey(groupingId)),
            strokeWidth: 0,
          }}
          height="14"
        />
        {contractEntityName}
      </span>
    );
  };

  const wafflesToppings = useMemo<ContractEntityServiceAreaDetailsWaffleToppings[]>(() => {
    return contractEntityDetails.map((ce) => ({
      /* oxlint-disable sort-keys */
      key: ce.contractEntityName.toLowerCase(),
      contractEntity: getContractEntityNameCell(ce.contractEntityName, ce.groupingId),
      providerCount: ce.providerCount.toLocaleString(),
      totalCases: ce.totalCases.toLocaleString(),
      casesPercentage: formatCasesPercentage(ce.totalCases, totalServiceAreaCases),
      groupingId: ce.groupingId,
      /* oxlint-enable sort-keys */
    }));
  }, [contractEntityDetails, totalServiceAreaCases, getColor, providerLandscapeContext.getContractEntityColorSlotKey]);

  const sortedWafflesToppings = useMemo(() => {
    return sortContractEntityDetailRows(wafflesToppings, sortedColumn, sortDirection);
  }, [wafflesToppings, sortedColumn, sortDirection]);

  const handleSort = (
    _: ContractEntityServiceAreaDetailsWaffleToppings[],
    column: keyof ContractEntityServiceAreaDetailsWaffleToppings,
    direction: SortDirections,
  ): ContractEntityServiceAreaDetailsWaffleToppings[] => {
    setSortedColumn(column);
    setSortDirection(direction);
    return _;
  };

  const onRowClick = (row: ContractEntityServiceAreaDetailsWaffleToppings) => {
    if (row.groupingId === providerFootprintContext.selectedContractEntity) {
      providerFootprintContext.setSelectedContractEntity(null);
    } else {
      providerFootprintContext.setSelectedContractEntity(row.groupingId);
    }
  };

  const rowDecorator = (row: ContractEntityServiceAreaDetailsWaffleToppings) =>
    row.groupingId === providerFootprintContext.selectedContractEntity ? "selected-contract-entity" : "";

  const loading = providerLandscapeContext.countySummaryData === undefined || providerFootprintContext.providerFootprintData === undefined;
  const isServiceAreaSelected = filtersContext.selectedCounties.length > 0;

  return (
    <div className={`market-expansion-waffles-container ${sortedWafflesToppings.length ? "table-has-data" : ""}`}>
      <Waffles<ContractEntityServiceAreaDetailsWaffleToppings>
        columns={columnConfig}
        customSort={handleSort}
        data={sortedWafflesToppings}
        emptyTableMessage={isServiceAreaSelected ? "No data available" : "Select a Service Area to see details"}
        fullWidth={true}
        loading={loading}
        onRowClick={onRowClick}
        rowDecorator={rowDecorator}
        passThroughSorting={true}
        sortDirectionOverride={sortDirection}
        sortedColumnOverride={sortedColumn}
        testId="market-expansion-contract-entity-details-waffle"
      />
    </div>
  );
};

export default ContractEntityServiceAreaDetailsTable;
